import { useContext, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { usePostHog } from '@posthog/react'
// useVideoState is a video-player helper that lives with the rest of the
// VideoJS accessors in OracleCommons (it's not PostHog-specific). The PostHog
// hook imports it to enrich the in-video-guide Layer 3 context with
// `playing_content_id` and `playing_feature_media_id` (the `playing_` prefix
// avoids collision with the recommendation events' `content_id` payload).
import { useVideoState } from '../OracleCommons/useVideoState'
import { EventContext, useChatSessionContext } from './PostHogProvider'
import type { PostHogEventName, PostHogEventSignatures } from './events'
import type { PostHogCallbackContext } from './events/context'

// Per-feature event-callback factories. Each lives next to its event-type
// definition under `events/<Feature>/` and returns the slice of the callback
// map for that feature.
import { createChatEventCallbacks } from './events/AI/chat'
import { createModerationEventCallbacks } from './events/AI/moderation'
import { createChannelEventCallbacks } from './events/Oracle/channels'
import { createDivinationEventCallbacks } from './events/Oracle/divination'
import { createPlaybackEventCallbacks } from './events/VideoPlayer/playback'
import { createHeaderEventCallbacks } from './events/Community/header'
import { createPageViewEventCallbacks } from './events/Community/pageViews'
import { createProfileEventCallbacks } from './events/Community/profile'
import { createPlaylistLibraryEventCallbacks } from './events/Community/playlistLibrary'
import { createDiscoverEventCallbacks } from './events/Community/discover'
import { createSharedLinkEventCallbacks } from './events/Community/sharedLink'
import { createCarouselErrorEventCallbacks } from './events/Community/carouselError'

/**
 * Type-safe hook for tracking PostHog events.
 * Returns a callback with the correct signature for the specified event.
 * Automatically includes context, playing_content_id, playing_feature_media_id,
 * and other common properties.
 *
 * Callbacks are stable across renders — fresh values are read from a ref so
 * callers can wrap usage in useCallback without invalidating other memos.
 *
 * Event-name typing comes from `events/<Feature>/`; each feature folder owns
 * its slice of `PostHogEventSignatures` and now also owns its slice of the
 * runtime callback map (via `create<Feature>EventCallbacks`).
 *
 * @example
 * // `aiMessageSent` is the AI-820 fan-out proof event: one call dispatches to
 * // BOTH PostHog and the GA4 Redux pipeline. The camelCase logical key drives
 * // the snake_case wire name (`ai_message_sent`) sent to both platforms.
 * const trackMessageSent = usePostHogEvent('aiMessageSent')
 * // Later, at call site (named-args object — AI-820):
 * trackMessageSent({
 *   message, inputMethod: 'Text', uiElement: 'Text Field', submissionMethod: 'keyboard',
 * })
 */
// More named exports may be added (selectors, helpers); a default export
// would force consumers into anonymous imports.
// eslint-disable-next-line import/prefer-default-export
export function usePostHogEvent<E extends PostHogEventName> (
  event: E,
): PostHogEventSignatures[E] {
  const posthogClient = usePostHog()
  const dispatch = useDispatch()
  const eventContext = useContext(EventContext)
  // `useVideoState` keeps its native field names; the rename to
  // `playingContentId` / `playingFeatureMediaId` happens here so consumers of
  // the callback context speak the Layer 3 vocabulary.
  const { contentId, featureMediaId, getPlaybackState } = useVideoState()
  const {
    chatSessionId,
    conversationTitle,
    aiPersona,
    chatExperienceName,
  } = useChatSessionContext()

  // Store latest values in a ref so callbacks can access fresh data without
  // needing to be recreated when dependencies change.
  const contextRef = useRef<PostHogCallbackContext>({
    posthogClient,
    eventContext,
    playingContentId: contentId,
    playingFeatureMediaId: featureMediaId,
    getPlaybackState,
    chatSessionId,
    conversationTitle,
    aiPersona,
    chatExperienceName,
    dispatch,
  })
  contextRef.current = {
    posthogClient,
    eventContext,
    playingContentId: contentId,
    playingFeatureMediaId: featureMediaId,
    getPlaybackState,
    chatSessionId,
    conversationTitle,
    aiPersona,
    chatExperienceName,
    dispatch,
  }

  // Build the full callback map once per hook instance by spreading each
  // per-feature factory. Every callback reads from contextRef on invocation,
  // so the empty-deps useMemo is safe — values stay fresh through the ref.
  const callbacks = useMemo(() => ({
    ...createChatEventCallbacks(contextRef),
    ...createModerationEventCallbacks(contextRef),
    ...createChannelEventCallbacks(contextRef),
    ...createDivinationEventCallbacks(contextRef),
    ...createPlaybackEventCallbacks(contextRef),
    ...createHeaderEventCallbacks(contextRef),
    ...createPageViewEventCallbacks(contextRef),
    ...createProfileEventCallbacks(contextRef),
    ...createPlaylistLibraryEventCallbacks(contextRef),
    ...createDiscoverEventCallbacks(contextRef),
    ...createSharedLinkEventCallbacks(contextRef),
    ...createCarouselErrorEventCallbacks(contextRef),
  }), [])

  return callbacks[event] as PostHogEventSignatures[E]
}
